import { MultiPolygon, Polygon } from "geojson";
import { z } from "zod";

import { fetchWithError, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams, isAggregated } from "@nens/shared-utils";

import { AssetKey, getGeom } from "../assets";
import { lizardDateSchema } from "../dates";
import { TimeseriesEvent } from "../types";

const rasterZonalSchema = z.object({
  results: z.array(
    z.object({
      time: lizardDateSchema.nullable(),
      value: z.number().nullable(),
    })
  ),
});

export type RasterZonal = z.infer<typeof rasterZonalSchema>;

export function useNontemporalRasterZonal(
  uuid: string,
  geometry: Polygon | MultiPolygon,
  assetKey?: AssetKey,
  params: Record<string, string | number | boolean> = {}
) {
  const geom = getGeom(geometry, assetKey);
  const url = combineUrlAndParams(`/api/v4/rasters/${uuid}/zonal/`, { ...params, geom });
  return useParsedQuery<RasterZonal>(
    ["lizard-api-v4", "rasterzonal", url],
    () => fetchWithError(url),
    (json: unknown) => rasterZonalSchema.parse(json)
  );
}

export async function fetchTemporalRasterPolygon(
  uuid: string,
  scale: string /* "ratio" | "interval", */,
  aggregation: string /* "average" | "sum" */,
  geometry: Polygon | MultiPolygon,
  start: Date,
  end: Date,
  frequency: string,
  timesteps: number,
  pixel_size: number | null,
  assetKey?: AssetKey,
  params: Record<string, string | number> = {}
) {
  const geom = getGeom(geometry, assetKey);
  const url = combineUrlAndParams(`/api/v4/rasters/${uuid}/zonal/`, {
    start: start.toISOString(),
    stop: end.toISOString(),
    geom,
    zonal_statistic: aggregation === "average" ? "mean" : "sum",
    auto_pixel_size: true,
    ...(isAggregated(timesteps) && {
      temporal_statistic: scale === "ratio" ? "sum" : "mean",
      frequency,
      closed: "right",
      label: "left",
    }),
    ...(pixel_size && { pixel_size }),
    ...params,
  });

  const json = await fetchWithError(url);
  const results = rasterZonalSchema.parse(json).results;
  return results as TimeseriesEvent[];
}
